import { useState, useCallback, useEffect } from 'react'
import { CookieManagerService } from '../../../services'

export interface SearchbarFields {
    adults: number | null
    kids: number | null
    arrival: Date | string | null
    departure: Date | string | null
    location: string
}

export interface SearchbarFieldsReturn {
    adults: number
    kids: number
    arrival: Date | null
    departure: Date | null
    location: string
}

type DateFields = 'arrival' | 'departure'
type NumberFields = 'adults' | 'kids'
type StringFields = 'location'

type UpdateFieldValue<T extends keyof SearchbarFields> = T extends DateFields
    ? Date | string | null
    : T extends NumberFields
    ? number
    : T extends StringFields
    ? string
    : never

export type UpdateSearchBarTransport = <T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => void

interface UseSearchbarReturn {
    searchbar: SearchbarFieldsReturn
    updateSearchbar: UpdateSearchBarTransport
}

export interface SearchbarProps {
    location?: string
    query?: {
        arrival?: string | Date | null
        departure?: string | Date | null
        kids?: string | number | null
        persons?: string | number | null
    }
}

const DEFAULT_VALUES: SearchbarFieldsReturn = {
    adults: 2,
    kids: 0,
    arrival: null,
    departure: null,
    location: '',
} as const

const convertToDate = (value?: Date | string | null): Date | null => {
    if (!value) {
        return null
    }
    return value instanceof Date ? value : new Date(value)
}

const useSearchbar = ({ location = '', query = {} }: SearchbarProps = {}): UseSearchbarReturn => {
    const [searchbar, setSearchbar] = useState<SearchbarFieldsReturn>(DEFAULT_VALUES)

    useEffect(() => {
        let cookieValues: string[] = []

        if (typeof window !== 'undefined') {
            const cookieString = CookieManagerService.getCookie(CookieManagerService.SEARCH_PARAMS)
            cookieValues = cookieString ? cookieString.split('|') : []
        }

        const updatedFields: SearchbarFieldsReturn = {
            arrival:
                query.arrival !== undefined && query.arrival !== null
                    ? convertToDate(query.arrival)
                    : cookieValues[0]
                    ? convertToDate(cookieValues[0])
                    : DEFAULT_VALUES.arrival,

            departure:
                query.departure !== undefined && query.departure !== null
                    ? convertToDate(query.departure)
                    : cookieValues[1]
                    ? convertToDate(cookieValues[1])
                    : DEFAULT_VALUES.departure,

            adults:
                query.persons && query.kids !== undefined
                    ? Number(query.persons) - Number(query.kids)
                    : query.persons !== undefined
                    ? Number(query.persons)
                    : cookieValues[2]
                    ? Number(cookieValues[2])
                    : DEFAULT_VALUES.adults,

            kids:
                query.kids !== undefined && query.kids !== null
                    ? Number(query.kids)
                    : query.persons !== undefined && (query.kids === undefined || query.kids === null)
                    ? DEFAULT_VALUES.kids
                    : cookieValues[3]
                    ? Number(cookieValues[3])
                    : DEFAULT_VALUES.kids,

            location: location && location !== '' ? location : cookieValues[4] || DEFAULT_VALUES.location,
        }

        setSearchbar(updatedFields)
    }, [query, location])

    const updateSearchbar = useCallback(<T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => {
        setSearchbar(prev => ({
            ...prev,
            [field]:
                field === 'arrival' || field === 'departure' ? convertToDate(value as Date | string | null) : value,
        }))
    }, [])

    return {
        searchbar,
        updateSearchbar,
    }
}

export default useSearchbar
